import { splitMergedDelta } from './productMerge';

const DB_NAME = 'bhima-pos-catalog';
const DB_VERSION = 1;
const STORE = 'items';

function finiteNumber(value, fallback) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * Partial catalog row. Images are never stored.
 * priceSource is local bookkeeping: a lot lookup must not be overwritten
 * by catalog META selling_price from updated_since.
 */
export function toCatalogRecord(product, { source = 'meta', previous } = {}) {
  const keepLotPrice = source === 'meta' && previous?.priceSource === 'lot';
  const text = (key, fallback = '') => {
    const incoming = product?.[key];
    if (incoming !== undefined && incoming !== null && incoming !== '') return incoming;
    if (previous?.[key] !== undefined && previous?.[key] !== null && previous[key] !== '') return previous[key];
    return fallback;
  };
  return {
    id: product.id,
    name: text('name'),
    sku: text('sku'),
    barcode: text('barcode'),
    status: product.status || previous?.status || 'ACTIVE',
    soldBy: product.soldBy === 'WEIGHT' || previous?.soldBy === 'WEIGHT'
      ? (product.soldBy || previous?.soldBy)
      : (product.soldBy || previous?.soldBy || 'EACH'),
    unit: text('unit', 'ea'),
    gstRate: finiteNumber(product.gstRate, finiteNumber(previous?.gstRate, 0)),
    price: keepLotPrice
      ? previous.price
      : finiteNumber(product.price, finiteNumber(previous?.price, 0)),
    mrp: keepLotPrice
      ? previous.mrp
      : finiteNumber(product.mrp, finiteNumber(product.price, finiteNumber(previous?.mrp, 0))),
    stock: finiteNumber(product.stock, finiteNumber(previous?.stock, 0)),
    catalogComplete: product.catalogComplete !== undefined
      ? product.catalogComplete !== false
      : previous?.catalogComplete !== false,
    onboardSource: product.onboardSource !== undefined ? (product.onboardSource || null) : (previous?.onboardSource || null),
    qtyStep: finiteNumber(product.qtyStep, finiteNumber(previous?.qtyStep, 1)),
    minOrderQty: finiteNumber(product.minOrderQty, finiteNumber(previous?.minOrderQty, 1)),
    maxOrderQty: finiteNumber(product.maxOrderQty, finiteNumber(previous?.maxOrderQty, 10)),
    updatedAt: product.updatedAt || previous?.updatedAt || null,
    priceSource: keepLotPrice ? 'lot' : source,
    barcodeAliases: Array.isArray(product.barcodeAliases)
      ? product.barcodeAliases
      : (previous?.barcodeAliases || []),
  };
}

export function activeCatalog(records) {
  return (records || []).filter((record) => record.status === 'ACTIVE');
}

/** Newest stored updated_at string. The delta bound is inclusive, so this value is sent as-is. */
export function newestUpdatedAt(records) {
  let best = null;
  let bestMs = -Infinity;
  (records || []).forEach((record) => {
    if (!record?.updatedAt) return;
    const ms = Date.parse(record.updatedAt);
    if (!Number.isFinite(ms) || ms < bestMs) return;
    bestMs = ms;
    best = record.updatedAt;
  });
  return best;
}

function requestToPromise(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function openCatalogDb() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' });
        store.createIndex('barcode', 'barcode', { unique: false });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export function createNativeCatalogStorage() {
  let dbPromise;
  const database = () => {
    if (!dbPromise) dbPromise = openCatalogDb();
    return dbPromise;
  };
  return {
    async getAll() {
      const db = await database();
      const tx = db.transaction(STORE, 'readonly');
      return requestToPromise(tx.objectStore(STORE).getAll());
    },
    async get(id) {
      const db = await database();
      const tx = db.transaction(STORE, 'readonly');
      const row = await requestToPromise(tx.objectStore(STORE).get(id));
      return row || null;
    },
    async putMany(records) {
      if (!records.length) return;
      const db = await database();
      await new Promise((resolve, reject) => {
        const tx = db.transaction(STORE, 'readwrite');
        const store = tx.objectStore(STORE);
        records.forEach((record) => store.put(record));
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error || new Error('catalog cache write aborted'));
      });
    },
    async getByBarcode(barcode) {
      const code = String(barcode || '').trim();
      if (!code) return [];
      const db = await database();
      const tx = db.transaction(STORE, 'readonly');
      const rows = await requestToPromise(tx.objectStore(STORE).index('barcode').getAll(code));
      return Array.isArray(rows) ? rows : [];
    },
    async deleteMany(ids) {
      if (!ids.length) return;
      const db = await database();
      await new Promise((resolve, reject) => {
        const tx = db.transaction(STORE, 'readwrite');
        const store = tx.objectStore(STORE);
        ids.forEach((id) => store.delete(id));
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error || new Error('catalog cache delete aborted'));
      });
    },
  };
}

export function createPosCatalogCache(storage) {
  let chain = Promise.resolve();
  /** In-memory barcode → ACTIVE row for O(1) scan hits after hydrate/upsert. */
  let activeByBarcode = new Map();

  const enqueue = (fn) => {
    const run = chain.then(fn, fn);
    chain = run.then(() => undefined, () => undefined);
    return run;
  };

  function rememberActiveBarcodes(rows) {
    (rows || []).forEach((row) => {
      if (!row?.id) return;
      // Drop stale barcode keys for this id (barcode edits / status changes).
      activeByBarcode.forEach((mapped, code) => {
        if (mapped.id === row.id) activeByBarcode.delete(code);
      });
      if (row.status !== 'ACTIVE') return;
      const trimmed = String(row.barcode || '').trim();
      if (trimmed) activeByBarcode.set(trimmed, row);
    });
  }

  function rebuildActiveBarcodes(rows) {
    activeByBarcode = new Map();
    rememberActiveBarcodes(activeCatalog(rows));
  }

  async function hydrate() {
    const rows = await storage.getAll();
    const list = Array.isArray(rows) ? rows : [];
    rebuildActiveBarcodes(list);
    return list;
  }

  async function upsert(records, { source = 'meta' } = {}) {
    return enqueue(async () => {
      const next = [];
      for (const record of records || []) {
        if (!record?.id) continue;
        // eslint-disable-next-line no-await-in-loop
        const previous = await storage.get(record.id);
        next.push(toCatalogRecord(record, { source, previous: previous || null }));
      }
      if (next.length) await storage.putMany(next);
      rememberActiveBarcodes(next);
      return next;
    });
  }

  async function remove(ids) {
    return enqueue(async () => {
      const list = [...new Set((ids || []).map((id) => String(id || '')).filter(Boolean))];
      if (!list.length) return;
      if (typeof storage.deleteMany === 'function') await storage.deleteMany(list);
      const drop = new Set(list);
      activeByBarcode.forEach((mapped, code) => {
        if (drop.has(mapped.id)) activeByBarcode.delete(code);
      });
    });
  }

  async function findByBarcode(barcode) {
    const code = String(barcode || '').trim();
    if (!code) return null;

    const hot = activeByBarcode.get(code);
    if (hot) return hot;

    if (typeof storage.getByBarcode === 'function') {
      try {
        const indexed = await storage.getByBarcode(code);
        const hit = activeCatalog(indexed).find((row) => String(row.barcode || '').trim() === code) || null;
        if (hit) {
          activeByBarcode.set(code, hit);
          return hit;
        }
        if (Array.isArray(indexed) && indexed.length) return null;
      } catch {
        // Fall through to full hydrate.
      }
    }

    const rows = await hydrate();
    return activeCatalog(rows).find((row) => String(row.barcode || '').trim() === code) || null;
  }

  /**
   * Empty cache: full list (include inactive). Otherwise GET delta from the newest stored updated_at.
   * client.listAllProducts(token, params) and client.listProductsUpdatedSince(token, updatedSince).
   */
  async function sync({ token, client, online = true } = {}) {
    const existing = await hydrate();
    if (!existing.length || !newestUpdatedAt(existing)) {
      const all = await client.listAllProducts(token, { includeInactive: true });
      await upsert(all, { source: 'meta' });
      return hydrate();
    }
    if (!online) return existing;
    const since = newestUpdatedAt(existing);
    const delta = await client.listProductsUpdatedSince(token, since);
    const { live, mergedIds } = splitMergedDelta(delta);
    if (mergedIds.length) await remove(mergedIds);
    if (live.length) await upsert(live, { source: 'meta' });
    return hydrate();
  }

  return {
    hydrate, upsert, remove, findByBarcode, sync, active: activeCatalog,
  };
}

export const posCatalogCache = createPosCatalogCache(createNativeCatalogStorage());
